import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid, Plus, Star, Trash2, Edit2, Check, X, ImageIcon, Monitor, Loader2, AlertCircle, AlertTriangle, Library } from 'lucide-react';
import { slideshowApi, overlaysApi, Slideshow, OverlayComposition } from '@/lib/api';
import { naturalCompare, sortByNatural } from '@/lib/utils';
import { ImageLibraryTab } from '@/components/slideshows/ImageLibraryTab';
import { useActiveFacilityId } from '@/hooks/useActiveFacilityId';
import { useAuth } from '@/store/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

// ── Overlay assignment helpers ────────────────────────────────────────────────

function getSlideshowWidgets(comp: OverlayComposition) {
  return (comp.scenes ?? []).flatMap(s =>
    (s.widgets ?? []).filter(w => w.type === 'slideshow')
  );
}

function getWidgetSlideshowId(widget: { properties: Record<string, unknown> }): number | null {
  const v = widget.properties['slideshowId'];
  return typeof v === 'number' ? v : null;
}

function getAssignedOverlays(sw: Slideshow, compositions: OverlayComposition[]) {
  const results: Array<{ name: string; sheetId: string }> = [];
  for (const comp of compositions) {
    const widgets = getSlideshowWidgets(comp);
    const hasExplicit = widgets.some(w => getWidgetSlideshowId(w) === sw.id);
    // A widget with no explicit assignment falls back to the facility-active slideshow
    const hasImplicit = sw.isDefault && widgets.some(w => getWidgetSlideshowId(w) === null);
    if (hasExplicit || hasImplicit) {
      results.push({ name: comp.name, sheetId: comp.sheetId });
    }
  }
  return results;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function SlideshowsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const facilityId = useActiveFacilityId();
  const queryClient = useQueryClient();

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Slideshow | null>(null);
  // ?tab=library so the library can be linked to directly.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'library' ? 'library' : 'slideshows';
  const setTab = (t: 'slideshows' | 'library') =>
    setSearchParams(t === 'library' ? { tab: 'library' } : {}, { replace: true });

  const { data, isLoading, isError, error: queryError } = useQuery({
    queryKey: ['slideshows', facilityId],
    queryFn: () => slideshowApi.list(facilityId ? { facilityId } : undefined),
    enabled: !isAdmin || !!facilityId,
  });
  const slideshows = (data?.slideshows ?? []).sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return naturalCompare(a.name, b.name);
  });

  const { data: compositionsData } = useQuery({
    queryKey: ['overlay-compositions', facilityId],
    queryFn: () => overlaysApi.listCompositions({ facilityId: facilityId! }),
    enabled: !!facilityId,
  });
  const compositions = sortByNatural(
    (compositionsData?.compositions ?? []).filter(c => getSlideshowWidgets(c).length > 0),
    c => c.sheetId
  );

  // ── Mutations ────────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (vars: { name: string; description?: string }) =>
      slideshowApi.create({ ...vars, ...(facilityId ? { facilityId } : {}) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slideshows'] });
      setCreating(false); setNewName(''); setNewDesc('');
    },
    onError: (e: any) => setError(e.message),
  });

  const setActiveMutation = useMutation({
    mutationFn: (id: number) => slideshowApi.setDefault(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['slideshows'] }),
    onError: (e: any) => setError(e.message),
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      slideshowApi.update(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slideshows'] });
      setRenamingId(null);
    },
    onError: (e: any) => setError(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => slideshowApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['slideshows'] }),
    onError: (e: any) => setError(e.message),
  });

  const assignOverlayMutation = useMutation({
    mutationFn: ({ widgetId, slideshowId }: { widgetId: number; slideshowId: number | null }) =>
      overlaysApi.assignSlideshow(widgetId, slideshowId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['overlay-compositions', facilityId] }),
    onError: (e: any) => setError(e.message),
  });

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleCreate = () => {
    if (!newName.trim()) return;
    createMutation.mutate({ name: newName.trim(), ...(newDesc.trim() ? { description: newDesc.trim() } : {}) });
  };

  const handleRename = (id: number) => {
    if (!renameValue.trim()) return;
    renameMutation.mutate({ id, name: renameValue.trim() });
  };

  const handleDelete = (sw: Slideshow) => {
    setDeleteTarget(sw);
  };

  const handleAssignOverlay = (widgetId: number, slideshowId: number | null) => {
    assignOverlayMutation.mutate({ widgetId, slideshowId });
  };

  // ── No-facility guard ────────────────────────────────────────────────────────

  if (isAdmin && !facilityId) {
    return (
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Slideshows</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Manage sponsor slideshows for each facility.</p>
        </div>
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 py-16 dark:border-gray-700">
          <LayoutGrid className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
          <p className="font-medium text-gray-500 dark:text-gray-400">No facility selected</p>
          <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">Select a facility in the sidebar to manage its slideshows.</p>
        </div>
      </div>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 p-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Slideshows</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            The <strong className="text-gray-700 dark:text-gray-300">active</strong> slideshow plays on all overlays unless a specific one is assigned.
          </p>
        </div>
        {!creating && tab === 'slideshows' && (
          <Button onClick={() => { setCreating(true); setError(null); }}>
            <Plus className="mr-2 h-4 w-4" />New Slideshow
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {([
          { key: 'slideshows', label: 'Slideshows', Icon: LayoutGrid },
          { key: 'library', label: 'Image Library', Icon: Library },
        ] as const).map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === key
                ? 'border-blue-600 text-blue-700 dark:border-blue-400 dark:text-blue-300'
                : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      {tab === 'library' ? (
        <ImageLibraryTab facilityId={facilityId} />
      ) : (<>

      {/* Error banner */}
      {error && (
        <div className="flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="ml-4 rounded p-0.5 hover:bg-red-100 dark:hover:bg-red-900/40">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Create form */}
      {creating && (
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-100">New Slideshow</h3>
          <div className="space-y-3">
            <input
              autoFocus
              type="text"
              placeholder="Slideshow name (e.g. Winter Sponsors)"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
            <input
              type="text"
              placeholder="Description (optional)"
              value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={handleCreate} disabled={!newName.trim() || createMutation.isPending} size="sm">
              {createMutation.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Create
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setCreating(false); setNewName(''); setNewDesc(''); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Slideshow list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 py-12 dark:border-red-800 dark:bg-red-900/10">
          <AlertCircle className="mb-2 h-8 w-8 text-red-400" />
          <p className="font-medium text-red-700 dark:text-red-400">Failed to load slideshows</p>
          <p className="mt-1 text-sm text-red-500">{(queryError as any)?.message ?? 'Unknown error'}</p>
        </div>
      ) : slideshows.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 py-16 dark:border-gray-700">
          <LayoutGrid className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
          <p className="font-medium text-gray-500 dark:text-gray-400">No slideshows yet</p>
          <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">Create your first slideshow to get started.</p>
          <Button className="mt-4" onClick={() => setCreating(true)}>
            <Plus className="mr-2 h-4 w-4" />New Slideshow
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {slideshows.map(sw => (
            <div
              key={sw.id}
              className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900"
            >
              {/* Icon */}
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                sw.isDefault
                  ? 'bg-green-100 dark:bg-green-900/40'
                  : 'bg-gray-100 dark:bg-gray-800'
              }`}>
                <LayoutGrid className={`h-4 w-4 ${sw.isDefault ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`} />
              </div>

              {/* Name + meta */}
              <div className="min-w-0 flex-1">
                {renamingId === sw.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleRename(sw.id);
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      className="w-48 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    />
                    <button onClick={() => handleRename(sw.id)} className="rounded p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20">
                      <Check className="h-4 w-4" />
                    </button>
                    <button onClick={() => setRenamingId(null)} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-gray-900 dark:text-gray-100">{sw.name}</span>
                    {sw.isDefault && (
                      <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 border-0 text-[10px] uppercase tracking-wide">
                        Active
                      </Badge>
                    )}
                  </div>
                )}
                <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-gray-400 dark:text-gray-500">
                  <span className="flex items-center gap-1">
                    <ImageIcon className="h-3 w-3" />
                    {sw._count?.images ?? 0} image{sw._count?.images !== 1 ? 's' : ''}
                  </span>
                  {sw.description && <span>· {sw.description}</span>}
                  {getAssignedOverlays(sw, compositions).map(a => (
                    <span key={a.sheetId} className="flex items-center gap-0.5 rounded bg-blue-900/30 px-1.5 py-0.5 text-[10px] font-medium text-blue-400">
                      <Monitor className="h-2.5 w-2.5" />{a.sheetId}
                    </span>
                  ))}
                </p>
              </div>

              {/* Actions */}
              <div className="flex shrink-0 items-center gap-1">
                <button
                  title={sw.isDefault
                    ? 'Deactivate (overlays set to Facility Active will show no slideshow)'
                    : 'Make this the active slideshow (replaces the current one)'}
                  onClick={() => setActiveMutation.mutate(sw.id)}
                  disabled={setActiveMutation.isPending}
                  className={`rounded p-1.5 transition-colors ${
                    sw.isDefault
                      ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                      : 'text-gray-400 hover:bg-gray-100 hover:text-amber-500 dark:hover:bg-gray-800 dark:hover:text-amber-400'
                  }`}
                >
                  <Star className={`h-4 w-4 ${sw.isDefault ? 'fill-current' : ''}`} />
                </button>
                <button
                  title="Rename"
                  onClick={() => { setRenamingId(sw.id); setRenameValue(sw.name); }}
                  className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <Link
                  to={`/slideshows/${sw.id}`}
                  className="ml-1 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  Manage →
                </Link>
                <button
                  title="Delete slideshow"
                  onClick={() => handleDelete(sw)}
                  disabled={deleteMutation.isPending}
                  className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Overlay Assignments ──────────────────────────────────────────────── */}
      {!!facilityId && (
        <div className="pt-2">
          <div className="mb-1 flex items-center gap-2">
            <Monitor className="h-4 w-4 text-gray-400" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Overlay Assignments</h2>
          </div>
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            Choose which slideshow plays on each overlay. <em>Facility Active</em> always uses whichever slideshow is marked active above.
          </p>

          {compositions.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 py-10 dark:border-gray-700">
              <Monitor className="mb-2 h-7 w-7 text-gray-300 dark:text-gray-600" />
              <p className="text-sm text-gray-400 dark:text-gray-500">
                No overlays with a slideshow widget found for this facility.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Overlay</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Sheet</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Assigned Slideshow</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {compositions.map(comp =>
                    getSlideshowWidgets(comp).map(widget => {
                      const currentId = getWidgetSlideshowId(widget);
                      return (
                        <tr key={`${comp.id}-${widget.id}`} className="bg-white dark:bg-gray-900">
                          <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{comp.name}</td>
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-400">Sheet {comp.sheetId}</td>
                          <td className="px-4 py-3">
                            <select
                              value={currentId ?? ''}
                              onChange={e => handleAssignOverlay(
                                widget.id,
                                e.target.value ? Number(e.target.value) : null
                              )}
                              disabled={assignOverlayMutation.isPending}
                              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            >
                              <option value="">— Facility Active —</option>
                              {slideshows.map(sw => (
                                <option key={sw.id} value={sw.id}>
                                  {sw.name}{sw.isDefault ? ' ★' : ''}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      </>)}

      {/* ── Delete confirmation modal ── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-xl border border-gray-700 bg-gray-900 p-6 shadow-xl">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-900/40">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-100">Delete "{deleteTarget.name}"?</h3>
                <p className="mt-1 text-sm text-gray-400">
                  The slideshow is deleted. Its images stay in the{' '}
                  <span className="font-semibold text-gray-200">Image Library</span>
                  {' '}for use in other slideshows. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  deleteMutation.mutate(deleteTarget.id, {
                    onSuccess: () => setDeleteTarget(null),
                    onError: () => setDeleteTarget(null),
                  });
                }}
              >
                {deleteMutation.isPending
                  ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Deleting…</>
                  : 'Delete Slideshow'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
